import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment, a as Host } from './index.js';
import { t as toValue, a as toLabel } from './p-CPuegN2k.js';
import { g as getFilterOptionsFn, h as handleOptions, i as isOtherOption } from './p-D36toVjT.js';
import { d as defineCustomElement$b, H as HiddenHTMLDataList } from './p-BY8GADaY.js';
import { d as defineCustomElement$i } from './p-BbTg3Car.js';
import { d as defineCustomElement$h } from './p-DzNXepTc.js';
import { d as defineCustomElement$g } from './p-C7Ewne5v.js';
import { d as defineCustomElement$f } from './p-B7ipNcUy.js';
import { d as defineCustomElement$e } from './p-C9_XXP_a.js';
import { d as defineCustomElement$d } from './p-Dpb0Xahb.js';
import { d as defineCustomElement$c } from './p-Da2lAZSv.js';
import { d as defineCustomElement$a } from './p-DUcqAgyP.js';
import { d as defineCustomElement$9 } from './p-CyejrxOi.js';
import { d as defineCustomElement$8 } from './p-ngrwPeVm.js';
import { d as defineCustomElement$7 } from './p-BU1hvS3W.js';
import { d as defineCustomElement$6 } from './p-DjGiWBv3.js';
import { d as defineCustomElement$5 } from './p-BtEI0Sh2.js';
import { d as defineCustomElement$4 } from './p-CT2oAZeh.js';
import { d as defineCustomElement$3 } from './p-CssqlikZ.js';
import { d as defineCustomElement$2 } from './p-CFAyJ2yW.js';
import { d as defineCustomElement$1 } from './p-ClSUnmWE.js';
import { d as debounce } from './p-Dg7s1ivP.js';

function isStaticOptionsArray(options) {
    return Boolean(options) && !(options instanceof Promise) && typeof options !== 'function';
}

const searchInputCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}";

const WuiSearchInput = /*@__PURE__*/ proxyCustomElement(class WuiSearchInput extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.choose = createEvent(this, "choose", 3);
        this.searchable = true;
        /**
         * Whether the input is a multi-select
         */
        this.multiselect = false;
        /**
         * Placeholder (displayed when `value` is undefined)
         */
        this.placeholder = '';
        /**
         * Disable component
         */
        this.disabled = false;
        /**
         * Show loading spinner
         *
         * Note: the loading state is automatically enabled when using async options
         */
        this.loading = false;
        /**
         * Display 'x' clear button when true
         */
        this.canClear = true;
        /**
         * Message to show when there are no search results
         *
         * Defaults to "No results"
         */
        this.noResultsMessage = 'No results';
        this.excludeOptions = [];
        /**
         * If menu is open
         */
        this.open = false;
        this.searchQuery = '';
        this.abortControllers = [];
        this.filterOptions = getFilterOptionsFn(this);
        this.filterOptionsDebounced = debounce(this.filterOptions, 250);
    }
    async watchOptions(newValue) {
        return handleOptions(this, newValue);
    }
    focus() {
        this.wuiInputElm?.focus();
    }
    async handleKeyDown(e) {
        // Handle keyboard navigating through MenuPane
        const wuiMenuPaneElement = this.elm.shadowRoot?.querySelector('wui-menu-pane');
        if (wuiMenuPaneElement && (await wuiMenuPaneElement.handleKeyDown(e))) {
            // Event was handled by menu pane
            this.open = !!wuiMenuPaneElement.open;
            return;
        }
        const wuiListElement = this.elm.shadowRoot?.querySelector('wui-list');
        if (!this.multiselect && wuiListElement && (await wuiListElement.handleKeyDown(e))) {
            return;
        }
        // If the list did not handle the 'Enter' key, then it means there
        // was no option to select. In that case we'll emit the `choose`
        // event with the current value as a non-existing option.
        if (!this.multiselect && e.key === 'Enter') {
            // Emit the input's current value
            this.chooseValue(this.value, this.value, false);
        }
    }
    componentWillLoad() {
        const old = {
            loading: this.loading,
            disabled: this.disabled,
        };
        this.loading = true;
        this.disabled = true;
        // Trigger options validation for initial value
        this.watchOptions(this.options).then(() => {
            this.loading = old.loading;
            this.disabled = old.disabled;
        });
    }
    toggleMenuPane(open = !this.open) {
        if (this.disabled || this.open === open) {
            return;
        }
        // If the input is multiselect and the menu is closing
        if (this.multiselect && this.open && !open) {
            this.chooseValue(this.value, this.selectedValue, true);
        }
        this.open = open;
    }
    setValue(newValue) {
        this.value = newValue;
        this.searchQuery = newValue; // Equal to value
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
    }
    chooseValue(value, selectedValue, exists) {
        // Emit the select event with the chosen option's value
        const event = this.choose.emit({ value, selectedValue, exists });
        if (!this.multiselect && !event.defaultPrevented) {
            // Clear input
            this.setValue('');
            // Close menu pane
            this.toggleMenuPane(false);
        }
    }
    handleSelect(option) {
        const newValue = toValue(option ?? '');
        // We pass in the new value as the search input value and the selected value, since this function is used for single select only.
        this.chooseValue(newValue, newValue, true);
    }
    handleCheckboxSelect(value, checklistInputElement) {
        this.value = value;
        this.selectedValue = checklistInputElement?.value;
    }
    handleQuery(e) {
        const { value } = e.target;
        // Prevent inner 'input' event from firing
        e.stopPropagation();
        e.preventDefault();
        this.setValue(value);
        this.filterOptionsDebounced();
        // Toggle menu based on value
        this.toggleMenuPane(!!this.value);
    }
    render() {
        const isStaticMultiselect = this.multiselect && isStaticOptionsArray(this.options);
        return (h(Host, { key: 'f3008294026184a1c1c0361009c65f9b7485bf41', tabIndex: 0, open: this.open }, h(HiddenHTMLDataList, { key: '9224eb58f9805045afd7d56d0138b9a3510d0504', id: this.name, options: this.optionsResolved, value: this.value }), h("wui-input", { key: '7ec73a00b969f3b9ade9cab3e62a407b727e9c4f', type: this.multiselect && isStaticOptionsArray(this.options) ? 'text' : 'search', list: this.name, value: this.value, placeholder: this.placeholder, loading: this.loading, name: this.name, disabled: this.disabled, error: this.error, canClear: this.canClear, icon: isStaticMultiselect ? 'chevron-down' : undefined, iconPosition: isStaticMultiselect ? 'trailing' : undefined, ref: (elm) => {
                this.wuiInputElm = elm;
            }, onInput: (e) => {
                this.handleQuery(e);
            }, onKeyDown: (e) => {
                this.handleKeyDown(e);
            }, onFocus: () => {
                if (isStaticMultiselect) {
                    this.toggleMenuPane(true);
                }
            } }), h("wui-menu-pane", { key: 'd292eb6524a65cddcff5053fbdd71bee2157a13f', open: this.open, stretch: "min", placement: "bottom", onClickOutside: () => {
                this.toggleMenuPane(false);
            }, onOpenChanged: (e) => {
                this.toggleMenuPane(e.detail);
            } }, this.multiselect ? (h("wui-check-list-input", { value: this.selectedValue
                ? typeof this.selectedValue === 'string'
                    ? [this.selectedValue]
                    : this.selectedValue
                : [], options: this.optionsResolved, onInput: (e) => this.handleCheckboxSelect(this.value, e.currentTarget), disabled: this.disabled || this.loading, list: { scrollSize: 'md' }, name: `${this.name}-multiselect` })) : (h("wui-list", { scrollSize: "md" }, this.optionsResolved?.length ? (this.optionsResolved.map((option, idx) => {
            if (isOtherOption(option)) {
                switch (option.type) {
                    case 'header':
                        return h("wui-list-item-header", { key: idx, ...option });
                    case 'divider':
                        return h("wui-list-item-divider", { key: idx });
                    case 'meta':
                        return h("wui-list-item-meta", { key: idx, ...option });
                }
            }
            return (h("wui-list-item", {
                // ❓ We shouldn't use an array index for unique key, but
                // for some reason, if we use the unique value of `option`,
                // the whole rendering gets messed up during list keyboard
                // navigation. No idea what's going on there...
                key: idx, ...(typeof option === 'object' ? option : {}), label: `${toLabel(option)}`, disabled: !this.open || (typeof option === 'object' ? option.disabled : false), onClick: () => this.handleSelect(option)
            }));
        })) : (h("wui-list-item-meta", { label: this.noResultsMessage })), (() => {
            if (!this.suggestionsAction) {
                return;
            }
            if (!this.value) {
                return;
            }
            const suggestionMatchesValue = this.optionsResolved?.some((s) => !isOtherOption(s) && toValue(s) === this.value);
            if (suggestionMatchesValue) {
                return;
            }
            return (h(Fragment, null, h("wui-list-item-divider", null), h("wui-list-item", { variant: "button", label: [
                    this.suggestionsAction.action,
                    ' "',
                    [this.value, 'bold'],
                    '"',
                    this.suggestionsAction.type ? ` ${this.suggestionsAction.type}` : '',
                ], icon: this.suggestionsAction.icon ?? 'plus', onClick: () => {
                    this.toggleMenuPane(false);
                    if (!this.value) {
                        return;
                    }
                    this.suggestionsAction?.onSelect(this.value);
                } })));
        })())))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "options": ["watchOptions"]
    }; }
    static get style() { return searchInputCss; }
}, [257, "wui-search-input", {
        "value": [1025],
        "selectedValue": [1025, "selected-value"],
        "name": [1],
        "multiselect": [516],
        "options": [16],
        "placeholder": [1],
        "disabled": [1540],
        "loading": [1540],
        "canClear": [4, "can-clear"],
        "error": [520],
        "noResultsMessage": [1, "no-results-message"],
        "suggestionsAction": [16, "suggestions-action"],
        "excludeOptions": [16, "exclude-options"],
        "open": [32],
        "searchQuery": [32],
        "optionsResolved": [32]
    }, undefined, {
        "options": ["watchOptions"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-search-input", "wui-box", "wui-check-input", "wui-check-list-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-search-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiSearchInput);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-check-list-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiSearchInput as W, defineCustomElement as d };
